import { addDays, formatLongDate, formatShortDate, weekdayOf } from './dates';
import { firstName } from './palette';
import type { Scope } from './permissions';
import { type Decline, canRespond, declines, presenceOf, presenceSummary } from './presence';
import { type Lookup, eventInfo, scopeInfo } from './schedule';
import type { ScheduleChange, ScheduleChangeKind } from './scheduleNotices';
import type { ReminderChannel, ScheduleEvent, Settings } from './types';

/**
 * Lembretes e cobranças automáticos (Configurações › Lembretes e cobranças):
 *
 * - **Lembrete** alguns dias antes da escala, para todo escalado numa escala publicada.
 * - **Cobrança** de quem ainda não confirmou: depois de `nudgeAfterDays` da publicação (ou
 *   da cobrança anterior), até `nudgeMax` vezes. O líder também pode cobrar na hora, no
 *   painel do evento.
 * - **Resumo semanal** para os líderes, toda segunda: vagas abertas, recusas e
 *   confirmações pendentes da semana, só da área de cada um.
 *
 * Aqui ficam as regras e os textos; quem envia (e guarda o que já foi enviado, para não
 * repetir) é o server.
 */

export const REMINDER_DAY_OPTIONS = [1, 2, 3, 7] as const;

export const REMINDER_CHANNEL_LABEL: Record<ReminderChannel, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
};

export const NUDGE_AFTER_RANGE = { min: 1, max: 7 } as const;
export const NUDGE_MAX_RANGE = { min: 1, max: 5 } as const;

/** Intervalo mínimo entre duas cobranças manuais do mesmo evento. */
export const NUDGE_COOLDOWN_MS = 3_600_000;

/** Dia da semana do resumo dos líderes (segunda). */
export const DIGEST_WEEKDAY = 1;

const DAY = 86_400_000;

export function daysBeforeLabel(days: number): string {
  if (days === 7) return '1 semana antes';

  return days === 1 ? '1 dia antes' : `${days} dias antes`;
}

/** "2 dias antes, por e-mail e WhatsApp" — a descrição do lembrete em Configurações. */
export function reminderSummary(settings: Settings): string {
  if (!settings.notifyReminders) return 'Desligado: ninguém recebe lembrete antes da escala.';

  const channels = settings.reminderChannels.map((c) => REMINDER_CHANNEL_LABEL[c].toLowerCase());
  const via = channels.length ? `, por ${channels.join(' e ')}` : ', sem canal escolhido';

  return `${daysBeforeLabel(settings.reminderDaysBefore)}${via}.`;
}

export interface ReminderContext {
  memberName: string;
  eventTitle: string;
  date: string;
  time: string;
  roleName: string;
  ministryName: string;
  /** Primeiros nomes de quem serve na mesma função. */
  mates: string[];
}

/** O que o lembrete diz a um escalado (ou `null`, se ele não está na escala). */
export function reminderContext(
  event: ScheduleEvent,
  memberId: string,
  lookup: Lookup
): ReminderContext | null {
  const roleId = Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0];
  const member = lookup.members.get(memberId);
  const found = roleId ? lookup.roles.get(roleId) : undefined;

  if (!roleId || !member || !found) return null;

  return {
    memberName: member.name,
    eventTitle: event.title,
    date: event.date,
    time: event.time,
    roleName: found.role.name,
    ministryName: found.ministry.name,
    mates: (event.slots[roleId] ?? [])
      .filter((id) => id !== memberId)
      .flatMap((id) => {
        const mate = lookup.members.get(id);

        return mate ? [firstName(mate.name)] : [];
      }),
  };
}

function scheduleLines(ctx: ReminderContext): string[] {
  return [
    `*${ctx.eventTitle}*`,
    `${formatLongDate(ctx.date)}, às ${ctx.time}`,
    `${ctx.ministryName} · ${ctx.roleName}`,
    ...(ctx.mates.length ? [`Com ${ctx.mates.join(', ')}`] : []),
  ];
}

/** Texto do lembrete (o mesmo no e-mail, no WhatsApp e na prévia de Configurações). */
export function reminderMessage(ctx: ReminderContext): string {
  return [
    `Olá, ${firstName(ctx.memberName)}! Lembrete da sua escala:`,
    '',
    ...scheduleLines(ctx),
    '',
    'Confirme sua presença ou avise que não pode ir pelo Live Gospel.',
  ].join('\n');
}

/** Texto da cobrança a quem ainda não respondeu. */
export function nudgeMessage(ctx: ReminderContext): string {
  return [
    `Olá, ${firstName(ctx.memberName)}! Você ainda não confirmou presença nesta escala:`,
    '',
    ...scheduleLines(ctx),
    '',
    'Confirme ou avise que não pode ir pelo Live Gospel — assim o líder consegue se organizar.',
  ].join('\n');
}

// ===== Mudança na escala publicada =====

/**
 * O e-mail (e o WhatsApp, quando houver provedor) de quem mudou numa escala publicada —
 * os mesmos casos do aviso no app, mais quem entrou nela. `null` sem a pessoa ou a função.
 */
export function changeMessage(
  change: ScheduleChange,
  event: ScheduleEvent,
  lookup: Lookup
): { subject: string; message: string } | null {
  const member = lookup.members.get(change.memberId);
  const found = lookup.roles.get(change.roleId);

  if (!member || !found) return null;

  const when = `${formatShortDate(event.date)} às ${event.time}`;
  const ctx: ReminderContext = reminderContext(event, change.memberId, lookup) ?? {
    memberName: member.name,
    eventTitle: event.title,
    date: event.date,
    time: event.time,
    roleName: found.role.name,
    ministryName: found.ministry.name,
    mates: [],
  };
  const hello = `Olá, ${firstName(member.name)}!`;
  const copy: Record<ScheduleChangeKind, { subject: string; intro: string; close: string }> = {
    removed: {
      subject: `Você saiu da escala: ${event.title}, ${when}`,
      intro: `${hello} Você não está mais escalado(a) nesta escala:`,
      close: 'Ficou alguma dúvida? Fale com o líder do ministério.',
    },
    moved: {
      subject: `Sua função mudou: ${event.title}, ${when}`,
      intro: `${hello} Sua função nesta escala mudou:`,
      close: 'Confira os detalhes e com quem você serve pelo Live Gospel.',
    },
    added: {
      subject: `Você voltou para a escala: ${event.title}, ${when}`,
      intro: `${hello} Você voltou para esta escala:`,
      close: 'Confirme sua presença ou avise que não pode ir pelo Live Gospel.',
    },
    assigned: {
      subject: `Você foi escalado(a): ${event.title}, ${when}`,
      intro: `${hello} Você foi escalado(a):`,
      close: 'Confirme sua presença ou avise que não pode ir pelo Live Gospel.',
    },
  };
  const { subject, intro, close } = copy[change.kind];

  return { subject, message: [intro, '', ...scheduleLines(ctx), '', close].join('\n') };
}

/**
 * O aviso a quem estava na escala publicada de uma data excluída sozinha da série ("Só
 * esta data" — o culto do feriado). Vai como uma mudança na escala (`DeliveryKind`
 * `change`).
 */
export function cancellationMessage(
  memberName: string,
  event: Pick<ScheduleEvent, 'title' | 'date' | 'time'>,
  reason: string
): { subject: string; message: string } {
  const when = `${formatShortDate(event.date)} às ${event.time}`;

  return {
    subject: `Evento cancelado: ${event.title}, ${when}`,
    message: [
      `Olá, ${firstName(memberName)}! Este evento não vai acontecer, e você não precisa mais servir nele:`,
      '',
      `*${event.title}*`,
      `${formatLongDate(event.date)} às ${event.time}`,
      ...(reason ? [`Motivo: ${reason}`] : []),
      '',
      'As outras datas continuam como estão. Ficou alguma dúvida? Fale com o líder do ministério.',
    ].join('\n'),
  };
}

// ===== Cobrança individual pelo WhatsApp =====

/**
 * O botão do WhatsApp ao lado de "Pendente", no painel do evento: escala publicada que
 * ainda vai acontecer, a pessoa escalada sem responder e com telefone. Quem pode vê-lo
 * (quem edita a função) é decidido pela tela.
 */
export function canWhatsApp(
  event: ScheduleEvent,
  member: { id: string; phone: string },
  today: string
): boolean {
  return canRespond(event, member.id, today) && presenceOf(event, member.id) === 'pending' && !!member.phone;
}

/** Mensagem pronta do WhatsApp (editável antes de abrir), assinada pelo primeiro nome de quem envia. */
export function whatsappMessage(ctx: ReminderContext, senderName: string): string {
  return [
    `Olá, ${firstName(ctx.memberName)}! Tudo bem?`,
    '',
    'Você está na escala e ainda não confirmou presença:',
    ...scheduleLines(ctx),
    '',
    'Consegue confirmar pelo Live Gospel ou me responder por aqui?',
    '',
    `Obrigado! ${firstName(senderName)}`,
  ].join('\n');
}

/** Link do WhatsApp com o número brasileiro (+55) e a mensagem preenchidos. */
export function whatsappLink(phone: string, text: string): string {
  return `https://wa.me/55${phone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
}

/** Exemplo fixo para a prévia em Configurações. */
export const SAMPLE_REMINDER: ReminderContext = {
  memberName: 'Ana Souza',
  eventTitle: 'Culto da Manhã',
  date: '2026-10-11',
  time: '10:00',
  roleName: 'Portaria',
  ministryName: 'Recepção',
  mates: ['Carlos', 'Marta'],
};

// ===== O que está para sair =====

/** Lembrete, cobrança ou o aviso de mudança na escala publicada (`change`). */
export type DeliveryKind = 'reminder' | 'nudge' | 'change';

/** Um envio já feito (o server guarda; um por pessoa, evento e tipo a cada vez). */
export interface Delivery {
  eventId: string;
  memberId: string;
  kind: DeliveryKind;
  at: number;
}

export interface Outgoing {
  kind: DeliveryKind;
  eventId: string;
  memberId: string;
}

function scheduledIds(event: ScheduleEvent): string[] {
  return [...new Set(Object.values(event.slots).flat())];
}

/**
 * Lembretes devidos: escalas publicadas que acontecem de hoje até `reminderDaysBefore`
 * dias à frente, para quem está escalado e ainda não recebeu o lembrete desse evento.
 * A janela (em vez do dia exato) garante o envio mesmo se uma rodada falhar.
 */
export function dueReminders(
  events: readonly ScheduleEvent[],
  settings: Settings,
  today: string,
  sent: readonly Delivery[]
): Outgoing[] {
  if (!settings.notifyReminders || settings.reminderChannels.length === 0) return [];

  const last = addDays(today, settings.reminderDaysBefore);
  const done = new Set(sent.filter((d) => d.kind === 'reminder').map((d) => `${d.eventId}:${d.memberId}`));

  return events
    .filter((e) => e.published && e.date >= today && e.date <= last)
    .flatMap((e) =>
      scheduledIds(e)
        .filter((id) => !done.has(`${e.id}:${id}`))
        .map((memberId): Outgoing => ({ kind: 'reminder', eventId: e.id, memberId }))
    );
}

/**
 * Cobranças devidas: escalas publicadas que ainda vão acontecer, para quem não respondeu,
 * `nudgeAfterDays` depois da publicação (ou da cobrança anterior), até `nudgeMax` vezes.
 */
export function dueNudges(
  events: readonly ScheduleEvent[],
  settings: Settings,
  today: string,
  publishedAt: ReadonlyMap<string, number>,
  sent: readonly Delivery[],
  now: number
): Outgoing[] {
  if (!settings.nudgePending || settings.reminderChannels.length === 0) return [];

  const wait = settings.nudgeAfterDays * DAY;

  return events
    .filter((e) => e.published && e.date > today)
    .flatMap((e) =>
      presenceSummary(e).pendingIds.flatMap((memberId): Outgoing[] => {
        const previous = sent.filter(
          (d) => d.kind === 'nudge' && d.eventId === e.id && d.memberId === memberId
        );

        if (previous.length >= settings.nudgeMax) return [];

        const since = Math.max(publishedAt.get(e.id) ?? now, ...previous.map((d) => d.at));

        return now - since >= wait ? [{ kind: 'nudge', eventId: e.id, memberId }] : [];
      })
    );
}

/** A cobrança manual do painel pode sair de novo? (uma por hora por evento) */
export function canNudgeAgain(event: ScheduleEvent, now: number): boolean {
  return event.nudgedAt === null || now - event.nudgedAt >= NUDGE_COOLDOWN_MS;
}

// ===== Resumo semanal dos líderes =====

export interface DigestItem {
  event: ScheduleEvent;
  /** Vagas sem ninguém (na área do líder). */
  open: number;
  declines: Decline[];
  /** Escalados que ainda não responderam (só em escala publicada). */
  pending: number;
}

/** Funções do recorte (`null` = todas). */
function scopeRoleIds(scope: Scope | null, lookup: Lookup): string[] | null {
  if (!scope) return null;
  if (scope.roleIds) return [...scope.roleIds];

  return [...lookup.roles.values()]
    .filter((r) => scope.ministryIds.includes(r.ministry.id))
    .map((r) => r.role.id);
}

/** O que o resumo de um líder traz: os eventos dos próximos 7 dias com algo a resolver. */
export function leaderDigest(
  events: readonly ScheduleEvent[],
  lookup: Lookup,
  scope: Scope | null,
  today: string,
  days = 7
): DigestItem[] {
  const last = addDays(today, days - 1);
  const roleIds = scopeRoleIds(scope, lookup);

  return events
    .filter((e) => e.date >= today && e.date <= last)
    .filter((e) => !scope || e.ministryIds.some((id) => scope.ministryIds.includes(id)))
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
    .map((event): DigestItem => {
      const info = scopeInfo(eventInfo(event, lookup), scope?.ministryIds ?? null, scope?.roleIds ?? null);

      return {
        event,
        open: info.pending.reduce((sum, p) => sum + p.missing, 0),
        declines: declines(event, roleIds),
        pending: event.published ? presenceSummary(event, roleIds).pending : 0,
      };
    })
    .filter((item) => item.open > 0 || item.declines.length > 0 || item.pending > 0);
}

/** O resumo é enviado hoje? (toda segunda) */
export function isDigestDay(today: string): boolean {
  return weekdayOf(today) === DIGEST_WEEKDAY;
}

/** Texto do resumo semanal (`''` quando não há nada a resolver). */
export function digestText(items: readonly DigestItem[], leaderName: string, lookup: Lookup): string {
  if (items.length === 0) return '';

  const lines = items.map((item) => {
    const parts = [
      item.open ? `${item.open} ${item.open === 1 ? 'vaga aberta' : 'vagas abertas'}` : '',
      item.declines.length
        ? `${item.declines.length} ${item.declines.length === 1 ? 'recusa' : 'recusas'}`
        : '',
      item.pending ? `${item.pending} sem confirmar` : '',
    ].filter(Boolean);
    const who = item.declines
      .map((d) => lookup.members.get(d.memberId)?.name)
      .filter(Boolean)
      .join(', ');

    return `• ${item.event.title}, ${formatShortDate(item.event.date)} às ${item.event.time}: ${parts.join(' · ')}${who ? ` (não podem ir: ${who})` : ''}`;
  });

  return [
    `Olá, ${firstName(leaderName)}! O resumo da semana nas escalas que você cuida:`,
    '',
    ...lines,
    '',
    'Abra o Escala para preencher as vagas e cobrar quem ainda não confirmou.',
  ].join('\n');
}
